// components/sections/contact/FAQAccordionItem.tsx
"use client";

import clsx from "clsx";
import { Plus } from "lucide-react";
import type { FAQItem } from "@/data/contact-page";

interface FAQAccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

/**
 * Pure-CSS accordion using the `grid-template-rows: 0fr -> 1fr` trick.
 * No JS height measurement (scrollHeight) needed, so toggling never
 * forces a synchronous layout read — this is what made the old GSAP
 * version stutter on click, especially with several items on the page.
 */
export function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
  index,
}: FAQAccordionItemProps) {
  return (
    <div
      className={clsx(
        "group border-b border-stone-200 transition-colors duration-300",
        index === 0 && "border-t",
        isOpen && "border-amber-200"
      )}
    >
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex w-full items-center gap-5 py-6 text-left"
        >
          <span
            className={clsx(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums transition-all duration-300",
              isOpen
                ? "bg-amber-100 text-amber-700"
                : "bg-stone-100 text-stone-400 group-hover:bg-stone-200 group-hover:text-stone-600"
            )}
          >
            {String(index + 1).padStart(2, "0")}
          </span>

          <span
            className={clsx(
              "flex-1 text-[15px] sm:text-[17px] font-semibold leading-snug transition-colors duration-200",
              isOpen ? "text-amber-800" : "text-stone-800 group-hover:text-stone-900"
            )}
          >
            {item.question}
          </span>

          <span
            className={clsx(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300",
              isOpen
                ? "rotate-45 border-amber-300 bg-amber-50 text-amber-600"
                : "border-stone-200 text-stone-400 group-hover:border-amber-200 group-hover:bg-amber-50/50 group-hover:text-amber-600"
            )}
          >
            <Plus size={14} strokeWidth={2.5} />
          </span>
        </button>
      </h3>

      <div
        className={clsx(
          "grid transition-[grid-template-rows] duration-400 ease-in-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="pb-7 pl-12 pr-14">
            <p className="text-[15px] leading-relaxed text-stone-500">
              {item.answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}